import { Card, DatePicker, Modal, Select, Spin } from "antd";
import React, { useEffect, useState } from "react";
import {
    dateRangePresets,
    disableFutureDates,
    formatDate,
} from "../../services/util";
import { FullscreenOutlined, LoadingOutlined } from "@ant-design/icons";
import { useQuery } from "react-query";
import HTTP from "../../services/httpService";
import { SupportTicketTable } from "./SupportTicketTable";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
    PieChart,
    Pie,
    Cell,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";
dayjs.extend(customParseFormat);

const PIE_COLORS = [
    "#3B82F6",
    "#22C55E",
    "#F59E0B",
    "#EF4444",
    "#8B5CF6",
    "#06B6D4",
];

/**
 * rawData from API: [ [header_label, header_count, header_id?], [name, count, id?], ... ]
 * Transform to recharts format: [ { name, value, id }, ... ]
 */
function transformPieData(rawData) {
    if (!rawData || rawData.length < 2) return [];
    return rawData.slice(1).map(([name, value, id]) => ({
        name: String(name),
        value: Number(value),
        id: id ?? null,
    }));
}

const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
        return (
            <div
                style={{
                    background: "#1e2433",
                    border: "1px solid #2e3650",
                    borderRadius: 8,
                    padding: "10px 14px",
                    fontSize: 13,
                    color: "#e0e6f0",
                }}
            >
                <p style={{ margin: 0, fontWeight: 600 }}>{payload[0].name}</p>
                <p style={{ margin: 0, color: payload[0].payload.fill }}>
                    Count: {payload[0].value}
                </p>
                <p style={{ margin: 0, color: "#8a94b0" }}>
                    {(payload[0].percent * 100).toFixed(1)}%
                </p>
            </div>
        );
    }
    return null;
};

const renderCustomLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
}) => {
    if (percent < 0.05) return null;
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
        <text
            x={x}
            y={y}
            fill="white"
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={12}
            fontWeight={600}
        >
            {`${(percent * 100).toFixed(1)}%`}
        </text>
    );
};

function PropertyPieChart({ data, height = 300, onSliceClick }) {
    return (
        <ResponsiveContainer width="100%" height={height}>
            <PieChart>
                <Pie
                    data={data}
                    cx="50%"
                    cy="50%"
                    outerRadius={height * 0.35}
                    dataKey="value"
                    labelLine={false}
                    label={renderCustomLabel}
                    onClick={(entry) => onSliceClick && onSliceClick(entry)}
                    style={{ cursor: onSliceClick ? "pointer" : "default" }}
                >
                    {data.map((_, index) => (
                        <Cell
                            key={`cell-${index}`}
                            fill={PIE_COLORS[index % PIE_COLORS.length]}
                        />
                    ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend
                    wrapperStyle={{ color: "#8a94b0", fontSize: 12 }}
                    formatter={(value) => (
                        <span style={{ color: "#c0c8e0" }}>{value}</span>
                    )}
                />
            </PieChart>
        </ResponsiveContainer>
    );
}

function HighestTicketPropertyChart() {
    const staff_id = localStorage.getItem("x-dashboard-staffid");
    const [chartData, setChartData] = useState([
        [`months`, "Success Percentage"],
    ]);
    const [enableZoom, setEnableZoom] = useState(false);
    const [componentList, setComponentList] = useState([]);
    const [chartBy, setChartBy] = useState("year");
    const [formData, setFormData] = useState({
        start_date: formatDate(dayjs().subtract(7, "day")),
        end_date: formatDate(dayjs()),
        staff_id,
        component_name: "",
        chart_by: "year",
    });
    const [selectedHotelForTableView, setSelectedHotelForTableView] =
        useState(0);

    const {
        status: componentStatus,
        data: componentsData,
        refetch: getComponents,
    } = useQuery(
        "hubspot-components",
        async () => await HTTP("/dashboard/getJiraComponents", { staff_id }),
        { enabled: false },
    );

    const {
        status: chartStatus,
        data: HightestTicketsPropertyChartData,
        refetch,
    } = useQuery(
        "HightestTicketsPropertyChart",
        async () =>
            await HTTP(`/dashboard/getPropertiesWithMostIssues`, {
                ...formData,
            }),
        { enabled: false },
    );

    useEffect(() => {
        getComponents();
    }, []);

    useEffect(() => {
        refetch();
    }, [formData]);

    useEffect(() => {
        if (componentStatus === "success")
            setComponentList(componentsData.data);
    }, [componentStatus, componentsData]);

    useEffect(() => {
        if (chartStatus === "success")
            setChartData(HightestTicketsPropertyChartData?.data);
    }, [chartStatus, HightestTicketsPropertyChartData]);

    const pieData = transformPieData(chartData);
    const isLoading = chartStatus !== "success";

    return (
        <Card className="box-shadow" style={{ width: "53%" }}>
            <div className="flex space-between">
                <strong>Top 5 property having max no of issues</strong>
                <div className="flex flex-gap-10 align-center">
                    <Select
                        value={chartBy}
                        defaultValue={chartBy}
                        placeholder="Chart By"
                        style={{ width: "100px" }}
                        onChange={(val) => {
                            setChartBy(val);
                            setFormData({ ...formData, chartBy: val });
                        }}
                    >
                        <Select.Option key="Year" value="year">
                            Year
                        </Select.Option>
                        <Select.Option key="Date" value="day">
                            Date
                        </Select.Option>
                    </Select>

                    {chartBy === "year" ? (
                        <DatePicker
                            allowClear={false}
                            picker="year"
                            defaultValue={dayjs()}
                            disabledDate={disableFutureDates}
                            placeholder="Year"
                            style={{ width: "100px" }}
                            onChange={(_, y) => {
                                setFormData({
                                    ...formData,
                                    start_date: formatDate(
                                        new Date(`${y}`, 0, 1),
                                    ),
                                    end_date: formatDate(
                                        new Date(`${y}`, 11, 31),
                                    ),
                                });
                            }}
                        />
                    ) : (
                        <DatePicker.RangePicker
                            defaultValue={[
                                dayjs(formData.start_date),
                                dayjs(formData.end_date),
                            ]}
                            onChange={(e) => {
                                setFormData({
                                    ...formData,
                                    start_date: formatDate(e[0]),
                                    end_date: formatDate(e[1]),
                                });
                            }}
                            allowClear={false}
                            disabledDate={disableFutureDates}
                            format="MM/DD/YYYY"
                            presets={dateRangePresets.filter(
                                (preset) =>
                                    preset.label !== "Today" &&
                                    preset.label !== "Yesterday",
                            )}
                            style={{ width: "250px" }}
                        />
                    )}

                    <Select
                        defaultValue={formData.component_name || null}
                        value={formData.component_name || null}
                        allowClear
                        showSearch
                        placeholder="Components"
                        filterOption={(input, option) =>
                            option.children
                                .toLowerCase()
                                .includes(input.toLowerCase())
                        }
                        onChange={(e) =>
                            setFormData({ ...formData, component_name: e })
                        }
                        loading={componentList.length === 0}
                        style={{ width: "250px" }}
                    >
                        {componentList.map((option) => (
                            <Select.Option
                                key={option.component_id}
                                value={option.component_name}
                            >
                                {option.component_name}
                            </Select.Option>
                        ))}
                    </Select>

                    <FullscreenOutlined
                        style={{ fontSize: "20px" }}
                        onClick={() => setEnableZoom(true)}
                    />
                </div>
            </div>

            {isLoading ? (
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        padding: "60px 0",
                    }}
                >
                    <Spin
                        indicator={
                            <LoadingOutlined style={{ fontSize: 70 }} spin />
                        }
                    />
                </div>
            ) : (
                <PropertyPieChart
                    data={pieData}
                    height={300}
                    onSliceClick={(entry) =>
                        setSelectedHotelForTableView(entry.id)
                    }
                />
            )}

            {/* Fullscreen Modal */}
            <Modal
                open={enableZoom}
                destroyOnHidden
                onCancel={() => setEnableZoom(false)}
                width="70vw"
                footer={null}
            >
                <div style={{ padding: "16px 0" }}>
                    <strong style={{ fontSize: 16 }}>
                        Top 5 property having max no of issues
                    </strong>
                    <PropertyPieChart data={pieData} height={500} />
                </div>
            </Modal>

            {/* Drill-down Table Modal */}
            <Modal
                open={Boolean(selectedHotelForTableView)}
                destroyOnHidden
                width="fit-content"
                footer={null}
                onCancel={() => setSelectedHotelForTableView(0)}
            >
                <SupportTicketTable
                    hotel_ids={[selectedHotelForTableView]}
                    start_date={formData.start_date}
                    end_date={formData.end_date}
                />
            </Modal>
        </Card>
    );
}

export default HighestTicketPropertyChart;
